import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Consistent interior-page hero. Navy by default (matching the homepage hero),
 * with an optional light variant. Title accepts nodes so pages can italicize a
 * highlight word (e.g. <>For <em>facilities.</em></>).
 */
export default function PageHero({
  eyebrow,
  title,
  lede,
  primary,
  secondary,
  breadcrumb,
  tone = 'dark',
}) {
  const reduce = useReducedMotion();
  const rise = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
        };

  return (
    <section className={`pg-hero ${tone === 'light' ? 'pg-hero--light' : ''} u-grain`}>
      <div className="pg-hero__glow" aria-hidden="true" />
      <div className="u-container pg-hero__inner">
        {breadcrumb && (
          <motion.nav className="pg-crumbs" aria-label="Breadcrumb" {...rise(0)}>
            {breadcrumb.map((c, i) => (
              <React.Fragment key={c.path || c.label}>
                {i > 0 && <span aria-hidden="true">/</span>}
                {c.path ? <Link to={c.path}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
              </React.Fragment>
            ))}
          </motion.nav>
        )}
        {eyebrow && (
          <motion.span className={`u-eyebrow ${tone === 'dark' ? 'u-eyebrow--on-dark' : ''}`} {...rise(0.05)}>
            {eyebrow}
          </motion.span>
        )}
        <motion.h1 className="pg-hero__title" {...rise(0.12)}>{title}</motion.h1>
        {lede && <motion.p className="pg-hero__lede" {...rise(0.2)}>{lede}</motion.p>}
        {(primary || secondary) && (
          <motion.div className="pg-hero__cta" {...rise(0.28)}>
            {primary && (
              <Link to={primary.href} className="btn btn--gold">
                {primary.label} <span className="btn__arrow" aria-hidden="true">→</span>
              </Link>
            )}
            {secondary && (
              <Link to={secondary.href} className={`btn ${tone === 'dark' ? 'btn--ghost-light' : 'btn--ghost'}`}>
                {secondary.label}
              </Link>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
